import { type FC, type FormEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, DialogHeader, ErrorMessage } from "@/shared/components";
import { useUser } from "@/partner/entities/auth";
import { interpolationFormat } from "@/partner/shared/lib";
import {
  OrganizationRoleType,
  useDisableOrganizationUserMutation,
  useEnableOrganizationUserMutation,
  useOrganizationUserQuery,
  useResendOrganizationUserInvitationMutation,
  useUpdateOrganizationUserRolesMutation,
} from "@/partner/entities/organizationUsers";
import { useRoleOptions } from "../model";

const FORM_ID = "organization-user-roles-form";

interface OrganizationUserDialogProps {
  open: boolean;
  organizationUserId: string | null;
  onClose: () => void;
}

type ConfirmAction = "disable" | "enable" | "resend" | null;
type MemberStatus = "active" | "disabled";

const STATUS_COLOR: Record<MemberStatus, "success" | "default"> = {
  active: "success",
  disabled: "default",
};

// Стиль диалога — как UserDialog/TagForm: Dialog fullWidth maxWidth="md",
// DialogHeader без "actions" (сохранение — кнопкой внутри карточки ролей),
// DialogContent sx={{ p: 1 }} → карточки Paper sx={{ p: 2 }} (подложка,
// как в остальных формах кабинета).
export const OrganizationUserDialog: FC<OrganizationUserDialogProps> = ({
  open,
  organizationUserId,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useUser();
  const roleOptions = useRoleOptions();

  const { data, loading } = useOrganizationUserQuery({
    variables: { id: String(organizationUserId) },
    skip: !organizationUserId || !open,
    fetchPolicy: "network-only",
  });

  const member = data?.organizationUser;

  const [roles, setRoles] = useState<OrganizationRoleType[]>([]);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  // При каждом открытии диалога/смене сотрудника начинаем с его актуальных
  // ролей, а не с состояния, оставшегося от предыдущего открытия.
  useEffect(() => {
    if (member) setRoles(member.roles);
  }, [member]);

  const refetchQueries = ["OrganizationUsersList", "OrganizationUser"];

  const [updateRoles, { loading: savingRoles, error: rolesError }] =
    useUpdateOrganizationUserRolesMutation({
      onCompleted: () => {
        enqueueSnackbar(t("team.member.saved"), { variant: "success" });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries,
    });

  const [disableOrganizationUser, { loading: disabling, error: disableError }] =
    useDisableOrganizationUserMutation({
      onCompleted: () => {
        setConfirmAction(null);
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries,
    });

  const [enableOrganizationUser, { loading: enabling, error: enableError }] =
    useEnableOrganizationUserMutation({
      onCompleted: () => {
        setConfirmAction(null);
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries,
    });

  const [resendInvitation, { loading: resending, error: resendError }] =
    useResendOrganizationUserInvitationMutation({
      onCompleted: () => {
        enqueueSnackbar(t("team.member.resent"), { variant: "success" });
        setConfirmAction(null);
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries,
    });

  const toggleRole = (role: OrganizationRoleType) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const canSaveRoles = roles.length > 0 && !member?.disabled;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!organizationUserId || !canSaveRoles) return;

    void updateRoles({ variables: { id: organizationUserId, roles } });
  };

  const isSelf = Boolean(member && member.user.id === currentUser.id);
  const isAdminRole = Boolean(
    member?.roles.includes(OrganizationRoleType.AdminType)
  );

  const title =
    member?.user.displayName || member?.user.email || t("team.member.title");

  const formatDate = (value: string): string =>
    interpolationFormat(value, "short", i18n.language);

  const status: MemberStatus | null = member
    ? member.disabled
      ? "disabled"
      : "active"
    : null;

  const handleConfirm = () => {
    if (!organizationUserId) return;

    if (confirmAction === "disable") {
      void disableOrganizationUser({ variables: { id: organizationUserId } });
    }

    if (confirmAction === "enable") {
      void enableOrganizationUser({ variables: { id: organizationUserId } });
    }

    if (confirmAction === "resend") {
      void resendInvitation({ variables: { id: organizationUserId } });
    }
  };

  const confirmTexts: Record<Exclude<ConfirmAction, null>, string> = {
    disable: t("team.member.confirmDisable"),
    enable: t("team.member.confirmEnable"),
    resend: t("team.member.resend"),
  };

  const confirmTitles: Record<Exclude<ConfirmAction, null>, string> = {
    disable: t("team.member.disable"),
    enable: t("team.member.enable"),
    resend: t("team.member.resend"),
  };

  const confirmLoading =
    confirmAction === "disable"
      ? disabling
      : confirmAction === "enable"
        ? enabling
        : resending;

  const confirmError =
    confirmAction === "disable"
      ? disableError
      : confirmAction === "enable"
        ? enableError
        : resendError;

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="md"
        scroll="body"
        open={open}
        onClose={onClose}
      >
        {(loading || savingRoles) && <LinearProgress />}
        <form id={FORM_ID} onSubmit={handleSubmit}>
          <DialogHeader
            title={title}
            onClose={onClose}
            loading={loading || savingRoles}
          />

          <DialogContent sx={{ p: 1 }}>
            {loading && !member && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {member && status && (
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("team.columns.member")}
                      </Typography>
                      <Typography variant="body1">
                        {member.user.email}
                      </Typography>
                      {member.user.displayName && (
                        <Typography variant="body2" color="text.secondary">
                          {member.user.displayName}
                        </Typography>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("team.columns.status")}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mt: 0.5 }}
                      >
                        <Chip
                          size="small"
                          color={STATUS_COLOR[status]}
                          label={t(`team.status.${status}`)}
                        />
                        {isSelf && (
                          <Chip size="small" label={t("team.status.you")} />
                        )}
                      </Stack>
                      {member.welcomeEmailSentAt && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {t("team.status.invitedAt", {
                            date: formatDate(member.welcomeEmailSentAt),
                          })}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    {rolesError && <ErrorMessage error={rolesError} />}

                    <Typography variant="caption" color="text.secondary">
                      {t("team.invite.roles")}
                    </Typography>

                    <FormGroup>
                      {roleOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          sx={{ alignItems: "flex-start", mb: 1 }}
                          control={
                            <Checkbox
                              checked={roles.includes(option.value)}
                              disabled={member.disabled}
                              onChange={() => toggleRole(option.value)}
                            />
                          }
                          label={
                            <Stack>
                              <Typography variant="body1">
                                {option.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {option.description}
                              </Typography>
                            </Stack>
                          }
                        />
                      ))}
                    </FormGroup>

                    {!member.disabled && (
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          variant="contained"
                          type="submit"
                          form={FORM_ID}
                          disabled={savingRoles || !canSaveRoles}
                        >
                          {t("team.member.save")}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {member.disabled ? (
                      <Button
                        variant="outlined"
                        onClick={() => setConfirmAction("enable")}
                      >
                        {t("team.member.enable")}
                      </Button>
                    ) : (
                      <>
                        <Tooltip
                          title={
                            isAdminRole ? t("team.member.lastAdminHint") : ""
                          }
                        >
                          <span>
                            <Button
                              variant="outlined"
                              color="error"
                              disabled={isSelf}
                              onClick={() => setConfirmAction("disable")}
                            >
                              {t("team.member.disable")}
                            </Button>
                          </span>
                        </Tooltip>

                        <Button
                          variant="outlined"
                          onClick={() => setConfirmAction("resend")}
                        >
                          {t("team.member.resend")}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            )}
          </DialogContent>
        </form>
      </Dialog>

      <ConfirmDialog
        open={confirmAction !== null}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        confirmTitle={confirmAction ? confirmTitles[confirmAction] : ""}
        confirmText={confirmAction ? confirmTexts[confirmAction] : ""}
        loading={confirmLoading}
        error={confirmError}
      />
    </>
  );
};
